import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Form, Formik } from 'formik';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { AccountData, CodeExceptionStatus, PassOut, PassService } from '../../../../api';
import { useAuth } from '../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../common/GeneralUtils';
import { getTimeFormat } from '../../../../common/getDateAndTimeFormat';
import { formatSecondsToTime, getDayName } from '../../../../utils/weekdayFormat';
import { useApiResponseHandlerContext } from '../../../../common/apiNotifications/useApiResponseHandlerContext';
import { ApiResponseHandlerType } from '../../../../common/apiNotifications/ApiResponseHandlerContext';
import { FormDatePicker } from '../../../../experienceSettings/formComponents/FormDatePicker';
import { DATE_FORMAT } from '../../../../calendar/calendarWrapper/CalendarWrapper';

type Props = {
  open: boolean;
  onClose: () => void;
  pass: PassOut;
  onUpdated: (pass: PassOut) => void;
};

export const ReleaseScheduleDialog = ({ open, onClose, pass, onUpdated }: Props) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const timeFormat = getTimeFormat(account);
  const { showResponseNotification } = useApiResponseHandlerContext();

  const alreadyReleased = !!pass.schedule_released_from;
  const [effectiveFrom, setEffectiveFrom] = useState<string>(
    pass.schedule_released_from ?? moment().format(DATE_FORMAT),
  );
  const [submitting, setSubmitting] = useState(false);
  const [inlineError, setInlineError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setEffectiveFrom(pass.schedule_released_from ?? moment().format(DATE_FORMAT));
    setInlineError(null);
  }, [open, pass.schedule_released_from]);

  const submit = async (nextEffectiveFrom: string | undefined) => {
    setSubmitting(true);
    setInlineError(null);
    try {
      const updated = await PassService.releasePassSchedule(pass.id, { effective_from: nextEffectiveFrom });
      onUpdated(updated);
      showResponseNotification(
        t(nextEffectiveFrom ? 'passes.release_schedule_success' : 'passes.release_schedule_undone'),
        ApiResponseHandlerType.SUCCESS,
      );
      onClose();
    } catch (e: any) {
      // pushing the release date out re-holds a window that was free, which the slot may no longer fit
      if (e?.body?.code === CodeExceptionStatus.PASS_SLOT_AT_CAPACITY) {
        const slot = `${getDayName(e.body.weekday, language)} ${formatSecondsToTime(e.body.start_time, timeFormat)}`;
        setInlineError(t('passes.release_schedule_slot_taken', { slot }));
      } else {
        showResponseNotification(t('saveHeader.errorGeneric'), ApiResponseHandlerType.ERROR);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {t(alreadyReleased ? 'passes.release_schedule_change_title' : 'passes.release_schedule_dialog_title')}
      </DialogTitle>
      <DialogContent>
        <Grid container flexDirection="column" gap={2}>
          <Typography variant="body2" color="text.secondary">
            {t(alreadyReleased ? 'passes.release_schedule_change_explanation' : 'passes.release_schedule_explanation')}
          </Typography>
          <Box>
            <Formik initialValues={{ effective_from: effectiveFrom }} enableReinitialize onSubmit={() => {}}>
              <Form>
                <FormDatePicker
                  fieldName="effective_from"
                  label={t('passes.release_schedule_effective_from')}
                  onChange={(value) => value && setEffectiveFrom(value.format(DATE_FORMAT))}
                  isFieldUpdate={false}
                  size="small"
                />
              </Form>
            </Formik>
          </Box>
          {inlineError && <Alert severity="error">{inlineError}</Alert>}

          <Alert severity="info">{t('passes.release_schedule_keeps_bookings')}</Alert>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          {t('common.cancel')}
        </Button>
        {alreadyReleased && (
          <Button color="inherit" onClick={() => submit(undefined)} disabled={submitting}>
            {t('passes.release_schedule_undo_cta')}
          </Button>
        )}
        <LoadingButton variant="contained" color="warning" onClick={() => submit(effectiveFrom)} loading={submitting}>
          {t(alreadyReleased ? 'passes.release_schedule_save_cta' : 'passes.release_schedule_cta')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
